import { useState } from 'react';
import { Alert, Button, Container, Group, ScrollArea, Stack } from '@mantine/core';
import { PartnerSelect } from '@ayocore/erp/crm/client/components/Partners/PartnerSelect';
import { useElitaDealsRepository } from '../../rpc';
import logger from '@ayocore/platform/common/helpers/logger';

interface Props extends Record<string, unknown> {
  /** ID исходной сделки (образец). */
  sourceDealId: number;
  /** Коллбек после успешного копирования — получает id/uuid новой сделки. */
  onCreated: (result: { id: number; uuid: string }) => void;
  onCancel: () => void;
}

/**
 * Форма «Создать КП по образцу».
 * Просит выбрать партнёра и вызывает ElitaDealsRepository.duplicateOrder.
 * Шапка и строки исходной сделки копируются в новую сделку со статусом «Новый».
 */
export function DuplicateElitaOrderForm({ sourceDealId, onCreated, onCancel }: Props) {
  const dealsRepo = useElitaDealsRepository();

  const [partnerId, setPartnerId] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!partnerId) {
      setError('Выберите партнёра');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await dealsRepo.duplicateOrder(sourceDealId, { partner_id: partnerId });
      onCreated(result);
    } catch (e) {
      logger.error('[Elita] Failed to duplicate order:', e);
      const message = e instanceof Error ? e.message : 'Не удалось скопировать КП';
      setError(mapDuplicateError(message));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '40dvh' }}>
      <ScrollArea style={{ flex: 1 }} scrollHideDelay={0}>
        <Container size='lg' px={{ base: 'xs', sm: 'sm' }} py='xs' maw='100%'>
          <Stack gap='xs' pb='md'>
            {error && (
              <Alert color='red' onClose={() => setError(null)} withCloseButton>
                {error}
              </Alert>
            )}

            <PartnerSelect
              label='Партнёр'
              value={partnerId?.toString() || ''}
              onChange={value => setPartnerId(value ? parseInt(value) : null)}
            />
          </Stack>

          <div
            style={{
              borderTop: '1px solid #e9ecef',
              backgroundColor: 'white',
              padding: '8px 0 8px 12px',
              position: 'sticky',
              bottom: 0,
              zIndex: 10,
            }}
          >
            <Group justify='flex-end'>
              <Button variant='default' onClick={onCancel} disabled={submitting}>
                Отмена
              </Button>
              <Button onClick={handleSubmit} loading={submitting} disabled={!partnerId}>
                Создать по образцу
              </Button>
            </Group>
          </div>
        </Container>
      </ScrollArea>
    </div>
  );
}

/** Переводит доменные коды ошибок duplicateOrder в человекочитаемые сообщения. */
function mapDuplicateError(code: string): string {
  switch (code) {
    case 'SOURCE_DEAL_NOT_FOUND':
      return 'Исходная сделка не найдена';
    case 'WRONG_DEAL_TYPE':
      return 'Эта сделка не является КП — копирование недоступно';
    case 'INVALID_PARTNER_ID':
      return 'Выберите партнёра';
    case 'ORDER_NEW_STATUS_NOT_FOUND':
      return 'Не найден статус «Новый» — обратитесь к администратору';
    default:
      return code || 'Не удалось скопировать КП';
  }
}
